import { useMemo, useState } from "react";
import { separarEspecialidad } from "../../utils/textoClinico";
import "../../styles/CitasRail.css";

/* ============================================================================
   RAIL DE ESPECIALIDADES

   Antes: ~23 tarjetas de 140x80 con el mismo icono y el nombre en MAYÚSCULAS
   partido en dos líneas, ocupando toda la pantalla. Encontrar una era leerlas
   todas, y la agenda quedaba bajo el pliegue.

   Ahora: una lista estrecha con buscador. Escala a 23 o a 80 sin crecer.

   QUÉ NO HACE, A PROPÓSITO:
   · No muestra contador de médicos. Ese dato NO existe en ninguna respuesta
     actual: pintarlo exigiría una llamada por especialidad al abrir la
     pantalla (23 peticiones), y "médicos con agenda hoy" ni siquiera es lo
     mismo que "médicos de la especialidad". Antes que inventarlo, no está.
   · No cambia el comportamiento: al elegir una especialidad se llama
     exactamente a la misma función que llamaba la tarjeta.
   ========================================================================== */

function IconoLupa() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
        </svg>
    );
}

function IconoLimpiar() {
    return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
        </svg>
    );
}

/**
 * Normaliza para buscar: sin tildes y en minúscula, para que "cardiologia"
 * encuentre "CARDIOLOGÍA" y al revés. El dato no se modifica.
 */
const normalizar = (texto) =>
    String(texto ?? "")
        .toLocaleLowerCase("es")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");

export default function RailEspecialidades({
    especialidades = [],
    idSeleccionada = null,
    onSeleccionar,
    // CITAS-CONTEO-ESPECIALIDAD: { idespecialidad: total } del día mostrado.
    // Objeto vacío = todavía no se sabe, y entonces NO se pinta ningún número:
    // un "0" mientras carga diría que no hay citas, que es distinto.
    conteos = {},
}) {
    const [filtro, setFiltro] = useState("");

    // El nombre se descompone UNA vez por especialidad, no en cada tecleo.
    const items = useMemo(
        () =>
            (Array.isArray(especialidades) ? especialidades : []).map((e) => {
                const crudo = e?.especialidades?.especialidad ?? "";
                const { nombre, sigla } = separarEspecialidad(crudo);
                return {
                    idespecialidad: e?.idespecialidad,
                    idclinica: e?.idclinica,
                    nombre,
                    sigla,
                    // Se busca sobre el nombre Y sobre el crudo: así "(CE)" o
                    // el nombre original en mayúsculas también encuentran.
                    busqueda: normalizar(`${nombre} ${sigla ?? ""} ${crudo}`),
                    original: e,
                };
            }),
        [especialidades]
    );

    const visibles = useMemo(() => {
        const q = normalizar(filtro).trim();
        if (!q) return items;
        // Todos los términos deben aparecer: "trau orto" encuentra
        // "Traumatologia Ortopedia".
        const terminos = q.split(/\s+/);
        return items.filter((i) => terminos.every((t) => i.busqueda.includes(t)));
    }, [items, filtro]);

    return (
        <aside className="crail" aria-label="Especialidades">
            <div className="crail__cabecera">
                <span className="crail__titulo">Especialidades</span>
                <span className="crail__total">
                    {filtro.trim() ? `${visibles.length} de ${items.length}` : items.length}
                </span>
            </div>

            <div className="crail__buscador">
                <IconoLupa />
                <input
                    className="crail__input"
                    type="search"
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                    placeholder="Filtrar"
                    aria-label="Filtrar especialidades"
                />
                {filtro && (
                    <button
                        type="button"
                        className="crail__limpiar"
                        onClick={() => setFiltro("")}
                        aria-label="Limpiar filtro"
                    >
                        <IconoLimpiar />
                    </button>
                )}
            </div>

            <div className="crail__lista">
                {visibles.length === 0 ? (
                    <p className="crail__vacio">
                        {items.length === 0
                            ? "Esta clínica no tiene especialidades activas."
                            : `Ninguna especialidad coincide con “${filtro.trim()}”.`}
                    </p>
                ) : (
                    visibles.map((item) => {
                        const activa = Number(item.idespecialidad) === Number(idSeleccionada);
                        return (
                            <button
                                key={item.idespecialidad}
                                type="button"
                                className={`crail__fila${activa ? " crail__fila--activa" : ""}`}
                                aria-current={activa ? "true" : undefined}
                                onClick={() => onSeleccionar?.(item.original)}
                            >
                                {activa && <span className="crail__barra" aria-hidden="true" />}
                                <span className="crail__nombre">{item.nombre}</span>
                                {item.sigla && <span className="crail__sigla">{item.sigla}</span>}
                                {/* El número sólo aparece si hay citas: una fila
                                    sin agenda ese día se queda limpia, y así el
                                    número resalta donde está el trabajo. */}
                                {conteos[item.idespecialidad] > 0 && (
                                    <span
                                        className={`crail__conteo${activa ? " crail__conteo--activa" : ""}`}
                                        title={`${conteos[item.idespecialidad]} cita${conteos[item.idespecialidad] === 1 ? "" : "s"} este día`}
                                    >
                                        {conteos[item.idespecialidad]}
                                    </span>
                                )}
                            </button>
                        );
                    })
                )}
            </div>
        </aside>
    );
}
